/**
 * Scheduled firmware update — the operator picks WHEN (2026-09-09).
 *
 * "A time where everyone sleeps, to avoid rebooting or a power cut" (Ray).
 * Everything here is in the OPERATOR's local time (the browser's); the
 * server stores UTC. Pure functions so the arithmetic is testable.
 */

/** The hour the quick picks land on: 2 AM, when a shop is closed. */
export const QUIET_HOUR = 2;

/** Tonight at 2 AM — i.e. the next 2 AM after `now`. */
export function nextQuietHour(now: Date): Date {
   const d = new Date(now);
   d.setHours(QUIET_HOUR, 0, 0, 0);
   if (d.getTime() <= now.getTime()) d.setDate(d.getDate() + 1);
   return d;
}

/** The 2 AM after that one. */
export function quietHourAfter(now: Date): Date {
   const d = nextQuietHour(now);
   d.setDate(d.getDate() + 1);
   return d;
}

/** For an <input type="datetime-local"> value: local time, no seconds. */
export function toLocalInputValue(d: Date): string {
   const p = (n: number) => String(n).padStart(2, '0');
   return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** "in 9 h" / "in 25 min" / "in 3 days" — how far off a moment is. */
export function untilLabel(at: Date, now: Date): string {
   const ms = at.getTime() - now.getTime();
   if (ms <= 0) return 'now';
   const min = Math.round(ms / 60000);
   if (min < 60) return `in ${min} min`;
   const h = Math.round(ms / 3600000);
   if (h < 48) return `in ${h} h`;
   return `in ${Math.round(ms / 86400000)} days`;
}

/** "2:00 AM, Wed Sep 10" — the moment in the operator's words. */
export function whenLabel(at: Date): string {
   const time = at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
   const day = at.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
   return `${time}, ${day}`;
}

/** Plain-English line for the row: "Update at 2:00 AM, Wed Sep 10 (in 9 h)". */
export function scheduledLine(scheduledAtIso: string, now: Date): string {
   const at = new Date(scheduledAtIso);
   return `Update at ${whenLabel(at)} (${untilLabel(at, now)})`;
}

/** "2:00 AM" for a stored quiet hour (shown on the viewer's clock format). */
export function quietHourLabel(q: { hour: number; minute: number }): string {
   const d = new Date(2026, 0, 1, q.hour, q.minute, 0, 0);
   return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
